/** 共通 UI コンポーネント（UI/UX設計書 §9.8） */
import {
  useEffect, useRef, type ButtonHTMLAttributes, type InputHTMLAttributes,
  type ReactNode, type SelectHTMLAttributes,
} from "react";

/* C-Btn */
export function Button({
  variant = "primary", size, loading, className = "", children, ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "ghost" | "danger";
  size?: "sm"; loading?: boolean;
}) {
  return (
    <button
      className={`btn btn--${variant} ${size ? `btn--${size}` : ""} ${className}`}
      disabled={rest.disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? "処理中…" : children}
    </button>
  );
}

/* C-Input */
export function TextInput({
  label, hint, error, id, ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: string; error?: string }) {
  const inputId = id ?? `in-${label}`;
  const describedBy = [hint && `${inputId}-hint`, error && `${inputId}-err`].filter(Boolean).join(" ");
  return (
    <div className="field">
      <label className="field__label" htmlFor={inputId}>
        {label}{rest.required && <span aria-hidden="true"> *</span>}
      </label>
      <input
        id={inputId} className="input" aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined} {...rest}
      />
      {hint && <span className="field__hint" id={`${inputId}-hint`}>{hint}</span>}
      {error && <span className="field__error" id={`${inputId}-err`}>✕ {error}</span>}
    </div>
  );
}

/* C-Select */
export function Select({
  label, children, id, ...rest
}: SelectHTMLAttributes<HTMLSelectElement> & { label: string }) {
  const selectId = id ?? `sel-${label}`;
  return (
    <div className="field">
      <label className="field__label" htmlFor={selectId}>{label}</label>
      <select id={selectId} className="select" {...rest}>{children}</select>
    </div>
  );
}

/* C-Badge */
export function Badge({ tone = "primary", children }: { tone?: string; children: ReactNode }) {
  return <span className={`badge badge--${tone}`}>{children}</span>;
}

/* C-Rating */
export function Rating({ value, count, interactive, onChange }: {
  value: number; count?: number; interactive?: boolean; onChange?: (v: number) => void;
}) {
  const rounded = Math.round(value);
  return (
    <span className="row" style={{ gap: 4 }}>
      <span aria-hidden="true" style={{ color: "var(--color-warning)", letterSpacing: 2 }}>
        {[1, 2, 3, 4, 5].map((n) =>
          interactive ? (
            <button
              key={n} type="button" onClick={() => onChange?.(n)}
              aria-label={`${n} つ星`}
              style={{
                background: "none", border: 0, cursor: "pointer", fontSize: "1.25rem",
                color: n <= rounded ? "var(--color-warning)" : "var(--border-strong)", padding: 2,
              }}
            >★</button>
          ) : (
            <span key={n} style={{ color: n <= rounded ? "var(--color-warning)" : "var(--border-strong)" }}>★</span>
          ),
        )}
      </span>
      <span className="muted">
        {value ? value.toFixed(1) : "—"}{count !== undefined && `（${count}件）`}
      </span>
    </span>
  );
}

/* C-Skeleton */
export function Skeleton({ height = 16, width = "100%", radius }: {
  height?: number | string; width?: number | string; radius?: number;
}) {
  return <div className="skeleton" style={{ height, width, borderRadius: radius }} aria-hidden="true" />;
}

/* C-Modal（§12.2 aria-modal / フォーカストラップ / Esc） */
export function Modal({ title, onClose, children, footer }: {
  title: string; onClose: () => void; children: ReactNode; footer?: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    ref.current?.querySelector<HTMLElement>("button, input, select, textarea, a[href]")?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "Tab" && ref.current) {
        const f = ref.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (!f.length) return;
        const first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("keydown", onKey); prev?.focus(); };
  }, [onClose]);

  return (
    <div className="scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-modal="true" aria-label={title} ref={ref}>
        <h3>{title}</h3>
        {children}
        <div className="row" style={{ justifyContent: "flex-end", marginTop: "var(--space-5)" }}>
          {footer ?? <Button variant="secondary" onClick={onClose}>閉じる</Button>}
        </div>
      </div>
    </div>
  );
}

/* C-Pagination（§9.8） */
export function Pagination({ total, page, limit, onChange }: {
  total: number; page: number; limit: number; onChange: (p: number) => void;
}) {
  const pages = Math.max(1, Math.ceil(total / limit));
  if (pages <= 1) return null;
  const around = [1, page - 1, page, page + 1, pages].filter((p) => p >= 1 && p <= pages);
  const uniq = [...new Set(around)].sort((a, b) => a - b);
  return (
    <nav className="row" style={{ justifyContent: "center", marginTop: "var(--space-5)" }} aria-label="ページ送り">
      <Button variant="ghost" disabled={page <= 1} onClick={() => onChange(page - 1)}>← 前へ</Button>
      {uniq.map((p, i) => (
        <span key={p} className="row" style={{ gap: 4 }}>
          {i > 0 && uniq[i - 1] !== p - 1 && <span className="muted">…</span>}
          <Button
            variant={p === page ? "primary" : "ghost"} size="sm"
            aria-current={p === page ? "page" : undefined} onClick={() => onChange(p)}
          >{p}</Button>
        </span>
      ))}
      <Button variant="ghost" disabled={page >= pages} onClick={() => onChange(page + 1)}>次へ →</Button>
    </nav>
  );
}

/* C-Stepper（§7.6 決済フロー） */
export function Stepper({ steps, current }: { steps: string[]; current: number }) {
  return (
    <ol className="row" style={{ listStyle: "none", padding: 0, margin: "0 0 var(--space-5)", flexWrap: "wrap" }}>
      {steps.map((s, i) => (
        <li key={s} className="row" aria-current={i === current ? "step" : undefined} style={{ gap: "var(--space-2)" }}>
          <span
            style={{
              width: 28, height: 28, borderRadius: "50%", display: "grid", placeItems: "center",
              background: i <= current ? "var(--color-primary)" : "var(--border-subtle)",
              color: i <= current ? "#fff" : "var(--text-muted)", font: "var(--font-small)", fontWeight: 700,
            }}
          >{i + 1}</span>
          <span style={{ color: i === current ? "var(--text-primary)" : "var(--text-muted)", fontWeight: i === current ? 700 : 400 }}>
            {s}
          </span>
          {i < steps.length - 1 && <span aria-hidden="true" className="muted" style={{ margin: "0 var(--space-3)" }}>—</span>}
        </li>
      ))}
    </ol>
  );
}

/* C-Tab */
export function Tabs({ items, value, onChange }: {
  items: { key: string; label: string }[]; value: string; onChange: (k: string) => void;
}) {
  return (
    <div role="tablist" className="row" style={{ borderBottom: "1px solid var(--border-subtle)", gap: 0, flexWrap: "wrap" }}>
      {items.map((it) => (
        <button
          key={it.key} role="tab" aria-selected={value === it.key}
          onClick={() => onChange(it.key)}
          style={{
            background: "none", border: 0, borderBottom: `3px solid ${value === it.key ? "var(--color-primary)" : "transparent"}`,
            padding: "var(--space-3) var(--space-4)", cursor: "pointer",
            color: value === it.key ? "var(--color-primary)" : "var(--text-secondary)",
            font: "var(--font-body)", fontWeight: value === it.key ? 700 : 400, minHeight: 44,
          }}
        >{it.label}</button>
      ))}
    </div>
  );
}

/* 空状態（§15） */
export function EmptyState({ message, action }: { message: string; action?: ReactNode }) {
  return (
    <div className="empty-state">
      <p>{message}</p>
      {action}
    </div>
  );
}
